import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { ArtworkPageModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/artworkPage/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/artworkPage/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/artworkPage/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/artworkPage/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/artworkPage/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/artworkPage/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/artworkPage/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/artworkPage/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/artworkPage/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/artworkPage/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/artworkPage/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/artworkPage/DELETE_FAILURE'

export type ArtworkPageModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: ArtworkPageModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function hydrateArtworkPage(artworkPage: ArtworkPageModel) {
  return (dispatch: AppDispatch) => {
    if (artworkPage?.id){
      const normalizedJson = normalize(artworkPage, Schemas.ARTWORK_PAGE)
      dispatch(updateEntities(normalizedJson))
    } else {
      console.error('Could not hydrate undefined artwork.')
    }
  }
}

export function loadArtworkPages(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().artworkPages.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/artwork_pages.json', options)
      .then(({ data }: { data: ArtworkPageModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK_PAGE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadArtworkPage(artworkPageId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.artworkPages.loadedForKeys]
    const loadedIds = [...state.artworkPages.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (artworkPageId && !loadedIds.includes(artworkPageId)){
      loadedIds.push(artworkPageId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/artwork_pages/${artworkPageId}.json`, options)
      .then(({ data }: { data: ArtworkPageModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function clearArtworkPageDataChanges(artworkPageId: number | string) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const { artworkPages } = getState().entities

    const updatedArtworkPage = {
      ...artworkPages[artworkPageId],
      data_changes: [],
    }

    const normalizedJson = normalize(updatedArtworkPage, Schemas.ARTWORK_PAGE)
    dispatch(updateEntities(normalizedJson))
  }
}

export function createArtworkPage(artworkPage: Partial<ArtworkPageModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      artwork_page: artworkPage,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/artwork_pages.json', options, config)
      .then(({ data }: { data: ArtworkPageModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateArtworkPage(
  artworkPage: UpdateParams<ArtworkPageModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      artwork_page: artworkPage,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/artwork_pages/${artworkPage.id}.json`, options, config)
      .then(({ data }: { data: ArtworkPageModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function buildArtworkPagePreview(
  artworkPageId: number,
  artworkBuildOptions: object,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    body: JSON.stringify({
      artwork_build_options: artworkBuildOptions,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/artwork_pages/${artworkPageId}/build_preview.json`, options, config)
      .then(({ data }: { data: ArtworkPageModel }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteArtworkPage(artworkPage: DeleteParams<ArtworkPageModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/artwork_pages/${artworkPage.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.ARTWORK_PAGE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: ArtworkPageModuleState = initialState,
  action: Action = { type: '' },
): ArtworkPageModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
